import React, { useEffect, useState } from 'react';
import api from '../api';

function ReleaseManagement() {
  const [releases, setReleases] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ assetId: '', amount: '', destinationAddress: '' });
  const [message, setMessage] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Check if current user can approve a specific step
  const canApprove = (requiredRole) => {
    if (user.role === 'admin') return true;
    return user.role === requiredRole;
  };

  useEffect(() => {
    fetchReleases();
    fetchAssets();
  }, []);

  const fetchReleases = async () => {
    try {
      const res = await api.get('/api/releases');
      setReleases(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAssets = async () => {
    try {
      const res = await api.get('/api/assets/my-assets');
      setAssets(res.data.filter(a => a.status === 'vault_storage'));
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      await api.post('/api/releases', {
        assetId: form.assetId,
        amount: parseFloat(form.amount),
        destinationAddress: form.destinationAddress,
      });
      setForm({ assetId: '', amount: '', destinationAddress: '' });
      setMessage('Release request created!');
      fetchReleases();
      fetchAssets();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to create release');
    }
  };

  // Approval handlers
  const handleApprove = async (releaseId, step) => {
    try {
      await api.post(`/api/releases/${releaseId}/approve-${step}`);
      fetchReleases();
    } catch (err) {
      alert(err.response?.data?.error || 'Approval failed');
    }
  };

  const getStepStatus = (release, step) => {
    const { status, riskApprovedBy, complianceApprovedBy, managementApprovedBy } = release;
    if (status === 'final_approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    switch (step) {
      case 'risk':
        return riskApprovedBy ? 'Approved' : status === 'pending_risk' ? 'Pending' : '—';
      case 'compliance':
        return complianceApprovedBy ? 'Approved' : status === 'risk_approved' ? 'Pending' : '—';
      case 'management':
        return managementApprovedBy ? 'Approved' : status === 'compliance_approved' ? 'Pending' : '—';
      case 'final':
        return status === 'final_approved' ? 'Approved' : 'Pending';
      default:
        return '—';
    }
  };

  const isStepPending = (release, step) => {
    return getStepStatus(release, step) === 'Pending';
  };

  // Check if the user can approve any step (to show the Action column)
  const showActionColumn = user.role === 'admin' || ['risk_officer', 'compliance_officer', 'manager'].includes(user.role);

  return (
    <div>
      <h2>Release Management</h2>

      {/* Create Release – visible only for non-admin users */}
      {user.role !== 'admin' && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>New Release Request</h3>
          {message && (
            <p style={{ color: message.includes('created') ? 'green' : 'red' }}>{message}</p>
          )}
          {assets.length === 0 ? (
            <p>No assets available for release (assets must be in vault storage).</p>
          ) : (
            <form onSubmit={handleCreate}>
              <select
                value={form.assetId}
                onChange={e => setForm({ ...form, assetId: e.target.value })}
                required
                style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
              >
                <option value="">Select asset</option>
                {assets.map(asset => (
                  <option key={asset.id} value={asset.id}>
                    Asset #{asset.id} – {asset.amount} ETH (Tx: {asset.txHash?.slice(0, 10)}...)
                  </option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Amount to release"
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                required
                style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
              />
              <input
                placeholder="Destination wallet address"
                value={form.destinationAddress}
                onChange={e => setForm({ ...form, destinationAddress: e.target.value })}
                required
                style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
              />
              <button
                type="submit"
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Submit Release Request
              </button>
            </form>
          )}
        </div>
      )}

      {/* Release List */}
      <div className="table-box">
        <h3>All Release Requests</h3>
        {releases.length === 0 ? (
          <p>No release requests yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Asset</th>
                <th>Amount</th>
                <th>To</th>
                <th>Risk</th>
                <th>Compliance</th>
                <th>Management</th>
                <th>Final</th>
                <th>Status</th>
                {showActionColumn && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {releases.map(r => (
                <tr key={r.id}>
                  <td>{r.Asset?.txHash?.slice(0, 8)}...</td>
                  <td>{r.amount} ETH</td>
                  <td>{r.destinationAddress?.slice(0, 8)}...</td>
                  <td style={{ color: getStepStatus(r, 'risk') === 'Approved' ? 'green' : 'orange' }}>
                    {getStepStatus(r, 'risk')}
                  </td>
                  <td style={{ color: getStepStatus(r, 'compliance') === 'Approved' ? 'green' : 'orange' }}>
                    {getStepStatus(r, 'compliance')}
                  </td>
                  <td style={{ color: getStepStatus(r, 'management') === 'Approved' ? 'green' : 'orange' }}>
                    {getStepStatus(r, 'management')}
                  </td>
                  <td style={{ color: getStepStatus(r, 'final') === 'Approved' ? 'green' : 'orange' }}>
                    {getStepStatus(r, 'final')}
                  </td>
                  <td style={{ color: r.status === 'final_approved' ? 'green' : r.status === 'rejected' ? 'red' : 'orange' }}>
                    {r.status.replace(/_/g, ' ')}
                  </td>
                  {showActionColumn && (
                    <td>
                      {isStepPending(r, 'risk') && canApprove('risk_officer') && (
                        <button
                          onClick={() => handleApprove(r.id, 'risk')}
                          style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', cursor: 'pointer', marginRight: '0.25rem' }}
                        >
                          Approve Risk
                        </button>
                      )}
                      {isStepPending(r, 'compliance') && canApprove('compliance_officer') && (
                        <button
                          onClick={() => handleApprove(r.id, 'compliance')}
                          style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', cursor: 'pointer', marginRight: '0.25rem' }}
                        >
                          Approve Comp
                        </button>
                      )}
                      {isStepPending(r, 'management') && canApprove('manager') && (
                        <button
                          onClick={() => handleApprove(r.id, 'management')}
                          style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', cursor: 'pointer', marginRight: '0.25rem' }}
                        >
                          Approve Mgmt
                        </button>
                      )}
                      {isStepPending(r, 'final') && canApprove('admin') && (
                        <button
                          onClick={() => handleApprove(r.id, 'final')}
                          style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', cursor: 'pointer' }}
                        >
                          Final Approve
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ReleaseManagement;